import { Store } from '@reduxjs/toolkit';
import BaseModule from 'sdk/base/base-module';

import { ActionTypes, sliceName } from './blogConfig';
import * as selectors from './blogSelector';
import FirebaseApi from '../firebase';
import { FIREBASE_TABLES } from '../firebase/firebaseConfig';
import { Blog, Blogs } from './blogInterface';
import UserApi from '../user';
import { createThumbnail, toFriendlyURI } from '@utils/index';

/**
 * @author Ahmad Igbaryya
 * @description Represent Blogs 
 *
 * @creationDate Mon Jan 27 2025
 */
export default class BlogApi extends BaseModule {
    public static instance: BlogApi;
    private firebaseApi = FirebaseApi.getInstance();
    constructor(store: Store) {
        if (BlogApi.instance) {
            return BlogApi.instance;
        }
        super(store, ActionTypes, sliceName);

        BlogApi.instance = this;
    }

    public static getInstance() {
        return BlogApi.instance;
    }

    newBlog = async (title: string, content: string, subtitle: string, image?: File | null) => {
        let newBlogId;
        try {

            const currentUser = UserApi.getInstance().getAppCurrentUser();
            if (!currentUser) {
                throw new Error('No user in context');
            }
            const { uid, canWrite } = currentUser;
            if (!canWrite || !uid) {
                throw new Error('User not authorized');
            }
            const friendlyURL = toFriendlyURI(title);
            const blog: Blog = {
                title,
                friendlyURL: friendlyURL,
                content,
                availableLanguages: 'en',
                createdBy: uid,
                subtitle,
                createdDate: this.firebaseApi.getServerTimestamp()
            };
            newBlogId = await this.firebaseApi.asyncPush(FIREBASE_TABLES.BLOGS, blog);
    
            if (image && newBlogId) {
                const thumbnailMinimized = await createThumbnail(image);
                const [thumbnailImage, blogImage] = await Promise.all([
                    this.firebaseApi.asyncStorageUpload(`${FIREBASE_TABLES.BLOGS}/${newBlogId}/thumbnailImage`, thumbnailMinimized, true),
                    this.firebaseApi.asyncStorageUpload(`${FIREBASE_TABLES.BLOGS}/${newBlogId}/blogImage`, image, true)
                ]);
                if (newBlogId && thumbnailImage && typeof thumbnailImage === 'string' && blogImage && typeof blogImage === 'string') {
                    await this.updateBlog(newBlogId, { thumbnailImage, image: blogImage });
                }
            }
            return this.sdkResponser.success();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            if (newBlogId) {
                this.deleteBlog(newBlogId);
            }
            throw new Error('Failed to create blog');
        }
    };
    updateBlog = async (blogId: string, blog: Partial<Blog>) => {
        delete blog.totalLikes;
        delete blog.totalReads;
        delete blog.currentUserLikedThis;
        delete blog.currentUserSavedThis;
        delete blog.currentUserReadThis;
        this.firebaseApi.asyncUpdate(`${FIREBASE_TABLES.BLOGS}/${blogId}`, blog);
    };
    loadBlogs = () => {
        const currentUser = UserApi.getInstance().getAppCurrentUser();
        this.firebaseApi.registerCall(FIREBASE_TABLES.BLOGS, async (snapshot: any) => {
            const allBlogs: Blogs = snapshot.val();
            const toSave: Blogs = {};
            const data = Object.entries(allBlogs || {});
            for (let i = 0 ; i < data.length ; i ++) {
                const [id, blog] = data[i];
                const userDisplayInfo = await UserApi.instance.retrieveUserDisplayInfo(blog.createdBy);
                const totalLikes = blog.likes ? Object.keys(blog.likes).length : 0;
                const totalReads = blog.reads ? Object.keys(blog.reads).length : 0;
                const currentUserSavedThis = !!currentUser?.savedBlogs?.[id]; 
                const currentUserLikedThis = !!currentUser?.likedBlogs?.[id];
                toSave[id] = {
                    ...blog,
                    totalReads,
                    totalLikes,
                    userDisplayInfo,
                    currentUserSavedThis,
                    currentUserLikedThis,
                    currentUserReadThis: currentUserLikedThis || currentUserSavedThis,
                    id,
                    isOwner: currentUser?.uid === blog.createdBy
                };
            }
            this.dispatchAction(ActionTypes.UPDATE_BLOGS, toSave);
        });
    };
    
    editBlog = async (blogId: string, title: string, content: string, subtitle: string, image?: File | null) => {
        const currentUser = UserApi.getInstance().getAppCurrentUser();
        if (!currentUser) {
            throw new Error('No user in context');
        }
        const { uid, canWrite } = currentUser;
        if (!canWrite || !uid) {
            throw new Error('User not authorized');
        }
        const friendlyURL = toFriendlyURI(title);
        const blog: Partial<Blog> = {
            title,
            friendlyURL: friendlyURL,
            content,
            availableLanguages: 'en',
            createdBy: uid,
            subtitle,
            updatedDate: this.firebaseApi.getServerTimestamp()
        };
        await this.updateBlog(blogId, blog);
        if (image) {
            const thumbnailMinimized = await createThumbnail(image);
            const [thumbnailImage, blogImage] = await Promise.all([
                this.firebaseApi.asyncStorageUpload(`${FIREBASE_TABLES.BLOGS}/${blogId}/thumbnailImage`, thumbnailMinimized, true),
                this.firebaseApi.asyncStorageUpload(`${FIREBASE_TABLES.BLOGS}/${blogId}/blogImage`, image, true)
            ]);
            if (thumbnailImage && typeof thumbnailImage === 'string' && blogImage && typeof blogImage === 'string') {
                await this.updateBlog(blogId, { thumbnailImage, image: blogImage });
            }
        }
        return this.sdkResponser.success();
    };

    /**
     * Retrieve a blog by identifier (can be blogId or friendlyURL)
     * Handles deep links by trying multiple lookup strategies
     */
    retrieveBlog = async (identifier?: string, friendlyURLParam?: string): Promise<Blog | null> => {
        // Support both old signature (blogId, friendlyURL) and new (identifier)
        const lookupId = identifier || friendlyURLParam;
        
        if (!lookupId) {
            return null;
        }
        
        const allBlogs = selectors.blogsSelector(this.store.getState());
        
        // Decode the lookupId in case it's URL encoded
        const decodedLookupId = decodeURIComponent(lookupId);
        
        // 1. First try to find in local store (by id, friendlyURL, or title)
        // Also handle partial matches for malformed friendlyURLs
        let blog = typeof allBlogs !== 'string' && Object.values(allBlogs || {}).find(
            (b) => b.id === lookupId || 
                   b.id === decodedLookupId ||
                   b.friendlyURL === lookupId || 
                   b.friendlyURL === decodedLookupId ||
                   b.friendlyURL?.startsWith(lookupId) ||
                   b.friendlyURL?.startsWith(decodedLookupId) ||
                   b.title === lookupId ||
                   b.title === decodedLookupId
        );
        
        // 2. If not in store, try fetching directly by ID from Firebase
        if (!blog) {
            const directFetch = await this.firebaseApi.asyncRead(`${FIREBASE_TABLES.BLOGS}/${lookupId}`);
            if (directFetch) {
                blog = {
                    id: lookupId,
                    ...directFetch
                };
            }
        }
        
        // 3. If still not found, search by friendlyURL (try both encoded and decoded)
        if (!blog) {
            let fromFB: Blogs | null = await this.firebaseApi.searchExact(
                `${FIREBASE_TABLES.BLOGS}`, 
                'friendlyURL', 
                decodedLookupId
            );
            
            // Also try with original lookupId if different
            if (!fromFB && lookupId !== decodedLookupId) {
                fromFB = await this.firebaseApi.searchExact(
                    `${FIREBASE_TABLES.BLOGS}`, 
                    'friendlyURL', 
                    lookupId
                );
            }
            
            if (fromFB) {
                const allFromFB = Object.keys(fromFB);
                if (allFromFB.length > 0) {
                    blog = {
                        id: allFromFB[0],
                        ...fromFB[allFromFB[0]]
                    };
                }
            }
        }
        
        // 4. Enrich with user display info and update store
        if (blog) {
            if (!blog.userDisplayInfo) {
                const userDisplayInfo = await UserApi.instance.retrieveUserDisplayInfo(blog.createdBy);
                blog.userDisplayInfo = userDisplayInfo || undefined;
            }
            
            // Calculate totals for deep-linked blogs
            if (blog.likes && blog.totalLikes === undefined) {
                blog.totalLikes = Object.keys(blog.likes).length;
            }
            if (blog.reads && blog.totalReads === undefined) {
                blog.totalReads = Object.keys(blog.reads).length;
            }
            
            if (blog.id) {
                let blogsToSave = {
                    [blog.id]: blog
                };
                if (typeof allBlogs !== 'string' && allBlogs) {
                    blogsToSave = {
                        ...allBlogs,
                        [blog.id]: blog
                    }; 
                }
                this.dispatchAction(ActionTypes.UPDATE_BLOGS, blogsToSave);
            }
            return blog;
        }
        return null;
    };

    deleteBlog = async (blogId?: string) => {
        if (!blogId) {
            throw new Error('No blog id provided');
        }
        const blog: Blog | null = await this.retrieveBlog(blogId);
        if (!blog) {
            return;
        }
        const { thumbnailImage } = blog;
        const currentUser = UserApi.getInstance().getAppCurrentUser();
        const { createdBy } = blog;
        if (currentUser?.uid !== createdBy) {
            throw new Error('User not authorized');
        }
        await this.firebaseApi.asyncDelete(`${FIREBASE_TABLES.BLOGS}/${blogId}`);
        if (thumbnailImage) {
            this.firebaseApi.asyncStorageDelete(`${FIREBASE_TABLES.BLOGS}/${blogId}/thumbnailImage`);
            this.firebaseApi.asyncStorageDelete(`${FIREBASE_TABLES.BLOGS}/${blogId}/blogImage`);
        }
        return this.sdkResponser.success();
    };

    getModuleErrors = () => {
        return selectors.blogErrorsSelector(this.store.getState());
    };

    saveBlog = async (blogId: string) => {
        const currentUser = UserApi.getInstance().getAppCurrentUser();
        if (!currentUser) {
            throw new Error('No user in context');
        }
        const { uid } = currentUser;
        await this.firebaseApi.asyncWrite(`${FIREBASE_TABLES.USERS}/${uid}/savedBlogs/${blogId}`, this.firebaseApi.getServerTimestamp());
    };
    unsaveBlog = async (blogId: string) => {
        const currentUser = UserApi.getInstance().getAppCurrentUser();
        if (!currentUser) {
            throw new Error('No user in context');
        }
        const { uid } = currentUser;
        await this.firebaseApi.asyncDelete(`${FIREBASE_TABLES.USERS}/${uid}/savedBlogs/${blogId}`);
    };
    shareBlog = (blogId: string) => {
        const allBlogs = selectors.blogsSelector(this.store.getState()) || {};
        if (typeof allBlogs === 'string') {
            return;
        }
        const blog = allBlogs[blogId] || Object.values(allBlogs || {}).find((b) => b.friendlyURL === blogId || b.id === blogId);
        if (!blog) {
            return;
        }
        const { title, id } = blog;
        
        // Use the blog ID for a clean, reliable URL
        const url = `${window.location.origin}/blog/${id || blogId}`;
        
        if (navigator.share) {
            navigator.share({
                title,
                text: title,
                url
            });
        } else {
            // Use clipboard API if available, fallback to execCommand
            if (navigator.clipboard) {
                navigator.clipboard.writeText(url);
            } else {
                const dummy = document.createElement('input');
                document.body.appendChild(dummy);
                dummy.value = url;
                dummy.select();
                document.execCommand('copy');
                document.body.removeChild(dummy);
            }
        }
    }; 
    likeBlog = async (blogId: string) => {
        const currentUser = UserApi.getInstance().getAppCurrentUser();
        if (!currentUser) {
            throw new Error('No user in context');
        }
        const { uid } = currentUser;
        await Promise.all([
            this.firebaseApi.asyncWrite(`${FIREBASE_TABLES.BLOGS}/${blogId}/likes/${uid}`, this.firebaseApi.getServerTimestamp()),
            this.firebaseApi.asyncWrite(`${FIREBASE_TABLES.USERS}/${uid}/likedBlogs/${blogId}`, this.firebaseApi.getServerTimestamp())
        ]);
    };

    dislikeBlog = async (blogId: string) => {
        const currentUser = UserApi.getInstance().getAppCurrentUser();
        if (!currentUser) {
            throw new Error('No user in context');
        }
        const { uid } = currentUser;
        await Promise.all([
            this.firebaseApi.asyncDelete(`${FIREBASE_TABLES.BLOGS}/${blogId}/likes/${uid}`),
            this.firebaseApi.asyncDelete(`${FIREBASE_TABLES.USERS}/${uid}/likedBlogs/${blogId}`)
        ]);
    };

    handleAction = (action: 'save' | 'share' | 'like' | 'dislike' | 'unsave', blogId: string) => {
        switch (action) {
            case 'save':
                return this.saveBlog(blogId);
            case 'unsave':
                return this.unsaveBlog(blogId);
            case 'share':
                return this.shareBlog(blogId);
            case 'like':
                return this.likeBlog(blogId);
            case 'dislike':
                return this.dislikeBlog(blogId);
            default:
                break;
        }
    };

    /**
     * Track a blog read. Uses user UID if logged in, otherwise generates a visitor ID.
     * Only tracks once per session per blog to avoid inflating read counts.
     */
    trackRead = async (blogId: string) => {
        try {
            // Get or generate visitor ID
            const currentUser = UserApi.getInstance().getAppCurrentUser();
            let visitorId = currentUser?.uid;
            
            if (!visitorId) {
                // For anonymous users, use a session-based visitor ID
                const storedVisitorId = sessionStorage.getItem('visitorId');
                visitorId = storedVisitorId || undefined;
                if (!visitorId) {
                    visitorId = `anon_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
                    sessionStorage.setItem('visitorId', visitorId);
                }
            }

            // Check if already tracked this blog in this session
            const sessionReadKey = `read_${blogId}`;
            if (sessionStorage.getItem(sessionReadKey)) {
                return; // Already tracked in this session
            }

            // Record the read
            await this.firebaseApi.asyncWrite(
                `${FIREBASE_TABLES.BLOGS}/${blogId}/reads/${visitorId}`,
                this.firebaseApi.getServerTimestamp()
            );

            // Mark as tracked in this session
            sessionStorage.setItem(sessionReadKey, 'true');
        } catch (err) {
            // Silently fail - don't interrupt user experience for analytics
            console.error('Failed to track read:', err);
        }
    };
}

